import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  IconDownload,
  IconFileCheck,
  IconFileX,
  IconCalendarExclamation,
  IconSpeakerphone,
  IconInfoCircle,
  IconX
} from '@tabler/icons-react';
import { downloadExport } from 'api/exports';
import { dismissNotification, setNotificationRead, type ReportNotification } from 'api/notifications';
import useUserPreferences from 'hooks/useUserPreferences';
import { formatUserDateTime } from 'utils/dateFormatter';

const presentations: Record<string, { icon: typeof IconInfoCircle; label: string }> = {
  ExportReady: { icon: IconFileCheck, label: 'Report ready' },
  ExportFailed: { icon: IconFileX, label: 'Report failed' },
  SchedulePaused: { icon: IconCalendarExclamation, label: 'Schedule needs attention' },
  ReleaseNotes: { icon: IconSpeakerphone, label: 'Release notes' },
  ReleaseNote: { icon: IconSpeakerphone, label: 'Release notes' }
};

export default function NotificationItem({ item, onDetails }: { item: ReportNotification; onDetails: () => void }) {
  const { userPreferences } = useUserPreferences();
  const [busy, setBusy] = useState<'download' | 'dismiss' | null>(null);
  const [error, setError] = useState('');
  const { icon: Icon, label } = presentations[item.type ?? ''] ?? { icon: IconInfoCircle, label: 'Notification' };
  const download = async () => {
    if (!item.relatedExportId || busy) return;
    setBusy('download');
    setError('');
    if (!item.readAt) void setNotificationRead(item.notificationId, true, item.tenantId).catch(() => {});
    try {
      await downloadExport(item.relatedExportId, item.tenantId);
    } catch (failure) {
      setError(
        failure && typeof failure === 'object' && 'message' in failure
          ? String(failure.message)
          : 'The file is unavailable. Open details for its current status.'
      );
    } finally {
      setBusy(null);
    }
  };
  const dismiss = async () => {
    if (busy) return;
    setBusy('dismiss');
    setError('');
    try {
      await dismissNotification(item.notificationId, item.tenantId);
    } catch {
      setError('Could not dismiss the notification. Please try again.');
    } finally {
      setBusy(null);
    }
  };
  return (
    <Stack spacing={1} sx={{ py: 1 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box component="span" role="img" aria-label={label} sx={{ pt: 1 }}>
          <Icon size={22} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Button
              disabled={!!busy}
              onClick={onDetails}
              sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', textAlign: 'left', px: 0 }}
            >
              <Typography sx={{ overflowWrap: 'anywhere' }} fontWeight={item.readAt ? 400 : 700}>
                {item.title}
              </Typography>
            </Button>
            {item.type === 'ExportReady' && item.relatedExportId && (
              <Tooltip title={busy === 'download' ? 'Downloading…' : 'Download'}>
                <span>
                  <IconButton color="primary" size="small" aria-label="Download" disabled={!!busy} onClick={() => void download()}>
                    <IconDownload size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={busy === 'dismiss' ? 'Dismissing…' : 'Dismiss notification'}>
              <span>
                <IconButton color="error" size="small" aria-label="Dismiss notification" disabled={!!busy} onClick={() => void dismiss()}>
                  <IconX size={18} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Typography variant="caption" display="block">
            {label} · {formatUserDateTime(item.createdAt, userPreferences)}
          </Typography>
        </Box>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
